"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";
import { clampBodyCount } from "@/lib/tool-kit/essay/body-count";
import {
  type EssayType,
  generateOutline,
  type OutlineSection,
} from "@/lib/tool-kit/essay/outline-steps";

/* ------------------------------------------------------------------ */
/*  Widget                                                             */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const ESSAY_OUTLINE_GENERATOR_STRINGS = {
  argumentative: "Argumentative",
  expository: "Expository",
  compareAndContrast: "Compare & Contrast",
  narrative: "Narrative",
  researchPaper: "Research Paper",
  persuasive: "Persuasive",
  essayType: "Essay Type",
  essayTopicThesis: "Essay Topic / Thesis",
  topicPlaceholder: "e.g., The impact of social media on mental health",
  numberOfBodyParagraphs: "Number of Body Paragraphs (2-5)",
  generateOutline: "Generate Outline",
  yourEssayOutline: "Your Essay Outline",
  copied: "Copied!",
  copyToClipboard: "Copy to Clipboard",
  yourTopicPlaceholder: "[Your Topic]",
  introduction: "Introduction",
  bodyParagraph: "Body Paragraph",
  conclusion: "Conclusion",
  bodyCountAdjusted:
    "The number of body paragraphs must be a whole number from 2 to 5, so the outline uses {count}.",
  introArgumentativeHook:
    "Hook: Start with a compelling fact, question, or bold statement related to your argument",
  introArgumentativeBackground: "Background: Provide context about {topic}",
  introArgumentativeOpposing:
    "Present the opposing viewpoint briefly to show awareness",
  introArgumentativeThesis:
    "Thesis Statement: State your clear position on {topic}",
  introExpositoryHook: "Hook: Begin with an interesting fact or statistic",
  introExpositoryBackground:
    "Background: Introduce the topic of {topic} and why it matters",
  introExpositoryTerms: "Define key terms if necessary",
  introExpositoryThesis:
    "Thesis Statement: State what you will explain about {topic}",
  introCompareHook:
    "Hook: Start with an observation about similarities or differences",
  introCompareBackground:
    "Background: Introduce both subjects related to {topic}",
  introCompareMeaningful: "Explain why this comparison is meaningful",
  introCompareThesis:
    "Thesis Statement: State the main point of your comparison",
  introNarrativeHook:
    "Hook: Open with a vivid scene, dialogue, or moment of tension",
  introNarrativeSetting:
    "Setting: Establish the time, place, and context of {topic}",
  introNarrativeCharacters: "Introduce the main characters or participants",
  introNarrativePurpose:
    "Thesis/Purpose: Hint at the significance of this story",
  introResearchHook:
    "Hook: Present a striking finding or gap in current knowledge",
  introResearchBackground:
    "Background: Review the context and significance of {topic}",
  introResearchQuestion: "State the research question or problem",
  introResearchThesis:
    "Thesis Statement: Present your central argument or hypothesis",
  introPersuasiveHook:
    "Hook: Open with a powerful quote, statistic, or rhetorical question",
  introPersuasiveBackground:
    "Background: Explain why {topic} is an urgent issue",
  introPersuasiveCredibility: "Establish your credibility on the subject",
  introPersuasiveThesis:
    "Thesis Statement: State your position and call to action regarding {topic}",
  bodyArgumentativeTopic:
    "Topic Sentence: Present supporting argument #{number}",
  bodyArgumentativeEvidence:
    "Evidence: Cite a specific source, study, or example",
  bodyArgumentativeAnalysis:
    "Analysis: Explain how this evidence supports your thesis",
  bodyArgumentativeCounter: "Address potential counterargument to this point",
  bodyArgumentativeTransitionNext:
    "Transition: Connect to the next supporting argument",
  bodyArgumentativeTransitionLast: "Transition: Lead into the conclusion",
  bodyExpositoryTopic:
    "Topic Sentence: Introduce aspect #{number} of your topic",
  bodyExpositoryExplanation: "Explanation: Provide detailed information",
  bodyExpositoryExample: "Example: Give a specific example or case study",
  bodyExpositoryAnalysis:
    "Analysis: Explain the significance of this information",
  bodyExpositoryTransitionNext: "Transition: Bridge to the next aspect",
  bodyExpositoryTransitionLast: "Transition: Lead into the conclusion",
  bodyCompareFirstTopic:
    "Topic Sentence: Introduce the first major point of comparison",
  bodyCompareFirstSubject1:
    "Subject 1: Describe how the first subject relates to this point",
  bodyCompareFirstSubject2:
    "Subject 2: Describe how the second subject relates to this point",
  bodyCompareFirstAnalysis:
    "Analysis: Discuss the significance of this similarity or difference",
  bodyCompareFirstTransition:
    "Transition: Connect to the next point of comparison",
  bodyCompareTopic: "Topic Sentence: Introduce point of comparison #{number}",
  bodyCompareSubject1: "Subject 1: Detail and evidence",
  bodyCompareSubject2: "Subject 2: Detail and evidence",
  bodyCompareAnalysis: "Analysis: Explain what this comparison reveals",
  bodyCompareTransitionNext: "Transition: Bridge to the next comparison point",
  bodyCompareTransitionLast: "Transition: Lead into the conclusion",
  bodyNarrativeFirstTopic: "Topic Sentence: Set up the rising action",
  bodyNarrativeFirstEvents: "Describe the key events or challenges that unfold",
  bodyNarrativeFirstDetails: "Include sensory details and dialogue",
  bodyNarrativeFirstCharacter:
    "Show character development or internal conflict",
  bodyNarrativeFirstTransition: "Transition: Build toward the climax",
  bodyNarrativeLastTopic:
    "Topic Sentence: Present the resolution or falling action",
  bodyNarrativeLastResolved: "Describe how the conflict is resolved",
  bodyNarrativeLastAftermath: "Show the aftermath and its impact",
  bodyNarrativeLastReflect: "Reflect on what was learned or changed",
  bodyNarrativeLastTransition: "Transition: Lead into the conclusion",
  bodyNarrativeMiddleTopic:
    "Topic Sentence: Continue the narrative arc (event #{number})",
  bodyNarrativeMiddleEvents: "Describe the events with vivid detail",
  bodyNarrativeMiddleDialogue: "Include dialogue or inner thoughts",
  bodyNarrativeMiddleTension: "Build tension and develop characters",
  bodyNarrativeMiddleTransition: "Transition: Move the story forward",
  bodyResearchTopic: "Topic Sentence: Present finding or argument #{number}",
  bodyResearchEvidence: "Evidence: Cite peer-reviewed sources and data",
  bodyResearchAnalysis: "Analysis: Interpret the evidence in context",
  bodyResearchDiscussion:
    "Discussion: Connect to your thesis and broader research question",
  bodyResearchTransitionNext:
    "Transition: Link to the next finding or argument",
  bodyResearchTransitionLast: "Transition: Prepare for the conclusion",
  bodyPersuasiveTopic: "Topic Sentence: Present persuasive point #{number}",
  bodyPersuasiveEvidence:
    "Evidence: Provide facts, statistics, or expert testimony",
  bodyPersuasiveEmotional:
    "Emotional Appeal: Connect to your audience's values or experiences",
  bodyPersuasiveRefutation:
    "Refutation: Address and dismiss a potential objection",
  bodyPersuasiveTransitionNext:
    "Transition: Build momentum toward the next point",
  bodyPersuasiveTransitionLast: "Transition: Lead into the conclusion",
  concArgumentativeRestate:
    "Restate Thesis: Rephrase your position in light of the evidence presented",
  concArgumentativeSummarize:
    "Summarize Key Arguments: Briefly recap your main supporting points",
  concArgumentativeImplications:
    "Address the broader implications of your argument",
  concArgumentativeClosing:
    "Closing Thought: End with a call to action or thought-provoking statement",
  concExpositoryRestate:
    "Restate Thesis: Summarize the main explanation in fresh words",
  concExpositoryRecap:
    "Recap Main Points: Highlight the key aspects you covered",
  concExpositorySignificance:
    "Broader Significance: Explain why this information matters",
  concExpositoryClosing:
    "Closing Thought: Leave the reader with a new perspective or question",
  concCompareRestate: "Restate Thesis: Reinforce your main comparison point",
  concCompareSummarize:
    "Summarize: Recap the most important similarities and differences",
  concCompareEvaluation:
    "Evaluation: State which subject is stronger or more effective (if applicable)",
  concCompareClosing:
    "Closing Thought: Reflect on what this comparison teaches us",
  concNarrativeReflect:
    "Reflect: Share the lesson or meaning of the experience",
  concNarrativeConnect: "Connect: Tie the story back to a universal theme",
  concNarrativeResolution: "Resolution: Provide a sense of closure",
  concNarrativeClosing:
    "Closing Thought: End with an image, feeling, or insight that lingers",
  concResearchRestate:
    "Restate Thesis: Summarize your central argument or findings",
  concResearchSynthesize:
    "Synthesize: Connect all findings into a coherent conclusion",
  concResearchLimitations:
    "Limitations: Acknowledge limitations of your research",
  concResearchFuture: "Future Directions: Suggest areas for further research",
  concResearchClosing:
    "Closing Thought: State the broader significance of your work",
  concPersuasiveRestate:
    "Restate Thesis: Reinforce your position with conviction",
  concPersuasiveSummarize: "Summarize: Recap the strongest arguments you made",
  concPersuasiveEmotional:
    "Emotional Closing: Make a final appeal to your audience",
  concPersuasiveCall:
    "Call to Action: Tell the reader exactly what they should do next",
} as const;

export default function EssayOutlineGeneratorWidget() {
  const [essayType, setEssayType] = useState<EssayType>("argumentative");
  const [topic, setTopic] = useState("");
  const [bodyCount, setBodyCount] = useState(3);
  const [outline, setOutline] = useState<OutlineSection[] | null>(null);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const strings = useToolUi(ESSAY_OUTLINE_GENERATOR_STRINGS);
  const fmt = useToolNumber();

  const essayTypes: { value: EssayType; label: string }[] = [
    { value: "argumentative", label: strings.argumentative },
    { value: "expository", label: strings.expository },
    { value: "compare-contrast", label: strings.compareAndContrast },
    { value: "narrative", label: strings.narrative },
    { value: "research", label: strings.researchPaper },
    { value: "persuasive", label: strings.persuasive },
  ];

  function handleGenerate() {
    const { count, adjusted } = clampBodyCount(bodyCount);
    setBodyCount(count);
    setNotice(
      adjusted ? t(strings.bodyCountAdjusted, { count: fmt(count) }) : null
    );
    setOutline(generateOutline(essayType, topic, count, strings));
    setCopied(false);
  }

  async function handleCopy() {
    if (!outline) {
      return;
    }

    const text = outline
      .map((section) => {
        const items = section.items.map((item) => `    ${item}`).join("\n");
        return `${section.heading}\n${items}`;
      })
      .join("\n\n");

    if (!(await writeToClipboard(text))) {
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <>
      {/* Controls */}
      <div className="space-y-5 rounded-lg border border-border bg-card p-6">
        {/* Essay type */}
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="eo-type"
          >
            {strings.essayType}
          </label>
          <select
            className="w-full rounded-lg border border-border bg-background p-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="eo-type"
            onChange={(e) => setEssayType(e.target.value as EssayType)}
            value={essayType}
          >
            {essayTypes.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Topic */}
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="eo-topic"
          >
            {strings.essayTopicThesis}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-background p-2.5 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="eo-topic"
            onChange={(e) => setTopic(e.target.value)}
            placeholder={strings.topicPlaceholder}
            type="text"
            value={topic}
          />
        </div>

        {/* Body paragraph count */}
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="eo-body"
          >
            {strings.numberOfBodyParagraphs}
          </label>
          <input
            className="w-32 rounded-lg border border-border bg-background p-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="eo-body"
            max={5}
            min={2}
            onChange={(e) => setBodyCount(Number(e.target.value))}
            type="number"
            value={bodyCount}
          />
        </div>

        <button
          className="rounded-lg bg-primary px-6 py-2.5 font-medium text-primary-foreground text-sm transition-colors hover:bg-primary/90"
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateOutline}
        </button>

        {notice !== null && (
          <p
            aria-live="polite"
            className="text-muted-foreground text-sm"
            id="eo-notice"
          >
            {notice}
          </p>
        )}
      </div>

      {/* Generated Outline */}
      {outline && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.yourEssayOutline}
            </h2>
            <button
              className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copyToClipboard}
            </button>
          </div>
          <div className="space-y-6 rounded-lg border border-border bg-muted/30 p-6">
            {outline.map((section) => (
              <div className="space-y-2" key={section.heading}>
                <h3 className="font-semibold text-base text-foreground">
                  {section.heading}
                </h3>
                <ul className="space-y-1.5 pl-6">
                  {section.items.map((item) => (
                    <li
                      className="text-muted-foreground text-sm leading-relaxed"
                      key={item}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
